import { Component, OnInit } from '@angular/core';
import { Product } from 'src/app/order/models/product.model';
import { ActivatedRoute } from '@angular/router';
import { ProductValidatorService } from '../../validators/product-validator.service';
import { ProductService, costOrNull } from '../../services/product.service';
import { take, catchError } from 'rxjs/operators';

@Component({
    selector: 'app-product-edit',
    templateUrl: './product-edit.component.html',
    styleUrls: ['./product-edit.component.scss'],
    standalone: false
})
export class ProductEditComponent implements OnInit {

  product: Product = new Product();

  /**
   * The cost as it was when the product loaded.
   *
   * The ordinary update route refuses a body whose cost differs from the stored value -
   * cost only moves through its own audited route - so the update has to post this back
   * unchanged, and the operator's new figure goes separately.
   */
  private loadedCost: number | null = null;

  isError: boolean = false;
  isServerError: boolean = false;
  isSuccess: boolean = false;

  constructor(private route: ActivatedRoute,
    private productValidator: ProductValidatorService,
    private productService: ProductService) { }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.loadProduct(+params['id']);
    })
  }

  loadProduct(id: number): void {
    this.productService.getProductById(id)
    .pipe(take(1))
    .pipe(catchError((e) => {
      this.isServerError = true;
      return e;
    }))
    .subscribe((product: Product) => {
      this.product = product;
      this.loadedCost = costOrNull(product.cost);
    })
  }

  onSubmit() {

    this.isError = false;
    this.isServerError = false;

    if(this.productValidator.isValid(this.product)) {

      // Hold cost at the stored value for the update, and remember what the operator
      // actually asked for. Sending a changed cost here would have the whole save
      // rejected, losing their other edits along with it.
      //
      // Pinned on a copy rather than on this.product, so the figure the operator typed
      // does not visibly snap back to the old one while the save is in flight.
      const requestedCost = costOrNull(this.product.cost);
      const payload: Product = Object.assign(new Product(), this.product, {cost: this.loadedCost});

      // try to save it
      this.productService.updateProduct(payload)
      .pipe(take(1))
      .pipe(catchError((e) => {
        this.isServerError = true;
        return e;
      }))
      .subscribe(() => {
        if(requestedCost !== this.loadedCost) {
          this.saveCost(requestedCost);
        } else {
          this.isSuccess = true;
        }
      })
    } else {
      this.isError = true;
    }
  }

  /**
   * Sends the new cost down its own route, which records it in the cost history.
   *
   * Runs after the update rather than before it: a repriced product whose other edits
   * were then rejected is harder to explain than a saved product that still shows the
   * old cost, and the operator can see which of the two happened.
   */
  private saveCost(cost: number | null): void {
    this.productService.setProductCost(this.product.id, cost, 'Changed on the product screen')
    .pipe(take(1))
    .pipe(catchError((e) => {
      // The product itself saved; only the reprice failed. Put the stored cost back on
      // screen so the form is not left showing a figure the ERP does not hold.
      this.isServerError = true;
      this.product.cost = this.loadedCost;
      return e;
    }))
    .subscribe(() => {
      this.loadedCost = cost;
      this.product.cost = cost;
      this.isSuccess = true;
    })
  }


}
